import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Check, X } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import {
  CourseDrawer,
  type CourseDrawerMode,
} from '../components/academics/CourseDrawer';
import {
  programLabel,
  type CourseSummary,
  type CourseWithLessons,
} from '../components/academics/types';

export const Route = createFileRoute('/_authed/academics/courses')({
  component: CoursesPage,
});

function CoursesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage = role === 'super_admin' || role === 'academic_admin';

  const [drawer, setDrawer] = useState<CourseDrawerMode | null>(null);

  const listQuery = useQuery({
    queryKey: ['courses', 'admin-list'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: CourseSummary[] }>('/api/courses', { signal }),
  });

  const detailQuery = useQuery({
    queryKey: ['courses', 'drawer-detail', drawer?.kind === 'edit' ? drawer.id : null] as const,
    enabled: drawer?.kind === 'edit',
    queryFn: ({ signal }) => {
      if (drawer?.kind !== 'edit') throw new Error('No course selected');
      return apiRequest<CourseWithLessons>(`/api/courses/${drawer.id}`, { signal });
    },
  });

  const columns = useMemo<ColumnDef<CourseSummary, unknown>[]>(() => {
    const base: ColumnDef<CourseSummary, unknown>[] = [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: (info) => (
          <span className="font-medium text-[color:var(--color-text)]">
            {info.row.original.name}
          </span>
        ),
      },
      {
        id: 'program',
        header: 'Program',
        cell: (info) => (
          <span className="text-[color:var(--color-text-muted)]">
            {programLabel(info.row.original.program)}
          </span>
        ),
      },
      {
        id: 'duration',
        header: 'Duration (weeks)',
        cell: (info) => {
          const c = info.row.original;
          const range =
            c.durationWeeksMin === c.durationWeeksMax
              ? `${c.durationWeeksMin}`
              : `${c.durationWeeksMin}–${c.durationWeeksMax}`;
          return (
            <span className="tabular-nums text-[color:var(--color-text-muted)]">
              {range}
            </span>
          );
        },
      },
      {
        id: 'sessionsPerWeek',
        header: 'Sessions / week',
        cell: (info) => (
          <span className="tabular-nums text-[color:var(--color-text-muted)]">
            {info.row.original.sessionsPerWeek}
          </span>
        ),
      },
      {
        id: 'sessionMinutes',
        header: 'Session length',
        cell: (info) => (
          <span className="tabular-nums text-[color:var(--color-text-muted)]">
            {info.row.original.sessionMinutes} min
          </span>
        ),
      },
      {
        id: 'isActive',
        header: 'Active',
        cell: (info) => (
          <span aria-label={info.row.original.isActive ? 'Active' : 'Inactive'}>
            {info.row.original.isActive ? (
              <Check size={16} className="text-[color:var(--color-success)]" aria-hidden="true" />
            ) : (
              <X size={16} className="text-[color:var(--color-text-muted)]" aria-hidden="true" />
            )}
          </span>
        ),
      },
    ];

    if (canManage) {
      base.push({
        id: 'edit',
        header: '',
        cell: (info) => (
          <div className="flex items-center justify-end">
            <EditIconButton
              onClick={() => setDrawer({ kind: 'edit', id: info.row.original.id })}
              label={`Edit ${info.row.original.name}`}
              title="Edit course"
            />
          </div>
        ),
      });
    }

    return base;
  }, [canManage]);

  const items = listQuery.data?.items ?? [];

  return (
    <ListOrOutlet basePath="/academics/courses">
    <ListShell
      title="Courses"
      description="Curriculum catalogue and lesson tracks."
      primaryAction={
        canManage ? (
          <Button
            onClick={() => setDrawer({ kind: 'create' })}
            icon={<Plus size={16} aria-hidden="true" />}
          >
            Add course
          </Button>
        ) : undefined
      }
    >
      {listQuery.isError ? (
        <ErrorState
          title="Could not load courses"
          description={
            listQuery.error instanceof ApiError ? listQuery.error.message : undefined
          }
          onRetry={() => listQuery.refetch()}
        />
      ) : (
        <DataTable<CourseSummary>
          data={items}
          columns={columns}
          isLoading={listQuery.isLoading && !listQuery.data}
          getRowId={(row) => row.id}
          onRowClick={(row) =>
            void navigate({
              to: '/academics/courses/$courseId',
              params: { courseId: row.id },
            })
          }
          emptyState={
            <EmptyState
              title="No courses yet"
              description={
                canManage
                  ? 'Add your first course to anchor batches and lessons.'
                  : 'Courses will appear here once an admin sets them up.'
              }
              {...(canManage
                ? {
                    action: (
                      <Button
                        onClick={() => setDrawer({ kind: 'create' })}
                        icon={<Plus size={16} aria-hidden="true" />}
                      >
                        Add course
                      </Button>
                    ),
                  }
                : {})}
            />
          }
        />
      )}

      {canManage ? (
        <CourseDrawer
          mode={drawer}
          onClose={() => setDrawer(null)}
          initial={detailQuery.data}
          isLoadingInitial={drawer?.kind === 'edit' && detailQuery.isLoading}
          loadError={detailQuery.error instanceof Error ? detailQuery.error : null}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['courses'] });
            void queryClient.invalidateQueries({ queryKey: ['course', 'detail'] });
            toast('Course saved.', { variant: 'success' });
            setDrawer(null);
          }}
        />
      ) : null}
    </ListShell>
    </ListOrOutlet>
  );
}
